import { UseMutateAsyncFunction } from "@tanstack/react-query";
import { format, formatDate } from "date-fns";
import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  CancelScheduleSendRounded,
  ErrorRounded,
  GroupsRounded,
  SpeedRounded,
  TimerRounded,
} from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import SubjectRoundedIcon from "@mui/icons-material/SubjectRounded";
import { Box, colors, Tooltip, Typography } from "@mui/material";
import { DataGridPro, GridCellParams, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";

import Overlay from "@components/Overlay/Overlay";
import RowActionsMenu from "@components/RowActionsMenu";
import ColumnHeader from "@components/Table/ColumnHeader";
import { getRowClassName, tableStyles } from "@components/Table/tableStyles";

import { CampaignType } from "@interfaces/campaign.type";
import { ContactType } from "@interfaces/contact.type";

import { handleGaugeValue } from "@pages/Campaigns/components/GaugeMetrics";

import { useCancelScheduledEmailQuery } from "@queries/campaign.query";

import { PopupActions } from "@store/popupStore";

import theme from "@theme/index";

import { exportCampaignContacts, exportCampaigns } from "../utils";

interface ICampaignsTableProperties {
  campaigns: CampaignType[];
  handleDeleteCampaignClick: (campaignId: string) => void;
  selection: string[];
  setSelection: (selection: string[]) => void;
}

export interface ICancelScheduledCampaignProperties {
  campaignId: string;
  cancelScheduledCampaign: UseMutateAsyncFunction<
    CampaignType,
    Error,
    {
      campaignId: string;
      technologyId: string;
    },
    CampaignType
  >;
  dateTime: string;
  technologyId: string;
}

const handleCancelScheduledCampaign = ({
  campaignId,
  cancelScheduledCampaign,
  dateTime,
  technologyId,
}: ICancelScheduledCampaignProperties) => {
  PopupActions.openConfirmation({
    content: (
      <Typography sx={{ color: theme.palette.text.primary, mt: 1 }}>
        Are you sure you would like to cancel sending this campaign on <strong>{dateTime}</strong>? You{" "}
        <strong>
          <u>cannot</u>
        </strong>{" "}
        undo this action.
      </Typography>
    ),
    customOnConfirmText: "Cancel Send",
    customOnRejectText: "Dismiss",
    isDanger: true,
    onConfirm: () => {
      if (!campaignId) return;
      void cancelScheduledCampaign({ campaignId, technologyId });
    },
    title: "Cancel Scheduled Send",
  });
};

export default function CampaignsTable({
  campaigns,
  handleDeleteCampaignClick,
  selection,
  setSelection,
}: ICampaignsTableProperties) {
  const [grey700] = [colors.grey[700]];
  const navigate = useNavigate();
  const parameters = useParams();
  const { mutateAsync: cancelScheduledCampaign, status: cancelCampaignStatus } = useCancelScheduledEmailQuery();

  const makeCampaignActionsMenuItems = useCallback(
    (campaignId: string) => [
      {
        func: () => handleDeleteCampaignClick(campaignId),
        label: (
          <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
            <DeleteIcon sx={{ color: theme.palette.grey[700] }} />
            <Typography variant="body2">Delete Draft</Typography>
          </Box>
        ),
      },
    ],
    [handleDeleteCampaignClick],
  );

  const makeSentCampaignActionsMenuItems = useCallback(
    (campaignId: string) => [
      {
        func: async () => {
          const currentCampaign = campaigns.find((campaign) => campaign.id === campaignId);

          if (!currentCampaign || !currentCampaign.emails) {
            // Campaign not found or it doesn't have an emails array (or emails array is null).
            return;
          }

          const contactsIds = currentCampaign.emails.map((email) => email.contactId);

          const contacts = currentCampaign.emails.map((emailData) => ({
            ...emailData,
            companyName: emailData.company.name,
            companyPrimaryDomain: emailData.company.primaryDomain,
            email: emailData.email,
            firstName: emailData.firstName,
            headline: emailData.headline,
            id: emailData.contactId,
            lastName: emailData.lastName,
            linkedinUrl: emailData.linkedinUrl,
            seniority: emailData.seniority,
            title: emailData.title,
          }));

          const campaignToExport = campaigns.find((campaign) => campaign.id === campaignId);

          if (!campaignToExport) {
            return;
          }

          await exportCampaignContacts(contactsIds, contacts as unknown as ContactType[], [campaignToExport]);
        },
        label: (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              gap: 1,
            }}
          >
            <GroupsRounded sx={{ color: theme.palette.grey[700] }} />
            <Typography variant="body2">Export Contacts</Typography>
          </Box>
        ),
      },
      {
        func: () => exportCampaigns([campaignId], campaigns),
        label: (
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              gap: 1,
            }}
          >
            <SpeedRounded sx={{ color: theme.palette.grey[700] }} />
            <Typography variant="body2">Export Metrics</Typography>
          </Box>
        ),
      },
    ],
    [campaigns],
  );

  const makeScheduledCampaignActionsMenuItems = useCallback(
    (campaignId: string, dateTime: string) => [
      {
        func: () => {
          if (parameters.technologyId) {
            void handleCancelScheduledCampaign({
              campaignId,
              cancelScheduledCampaign,
              dateTime,
              technologyId: parameters.technologyId,
            });
            // void cancelScheduledCampaign({ campaignId, technologyId: parameters.technologyId });
          }
        },
        label: (
          <Box
            sx={{
              alignItems: "center",
              color: grey700,
              display: "flex",
              gap: 1,
            }}
          >
            <CancelScheduleSendRounded />
            Cancel Campaign Send
          </Box>
        ),
      },
    ],
    [handleDeleteCampaignClick, navigate],
  );

  // const formatDatetime = (date: string) => {
  //   if (!date) return "-";
  //   return format(new Date(date), "MM/dd/yy'@'h:mm a");
  // };

  const handleCellClick = (parameters: GridCellParams<CampaignType>) => {
    // if the field is not name, return
    if (parameters.field !== "name") return;

    // if campaign is scheduled, navigate to performance tab
    if (parameters.row.scheduledAt) {
      navigate(`/campaigns/${parameters.id}/performance`);
      return;
    }

    // if campaign is draft, navigate to account tab
    if (!parameters.row.sentCount) {
      navigate(`/campaigns/create/${parameters.id}/account`);
      return;
    }

    // if campaign is sent, navigate to performance tab
    if (parameters.row.sentCount) {
      navigate(`/campaigns/${parameters.id}/performance`);
    }
  };

  const clickableStyles = useMemo(
    () => ({
      color: colors.blue[600],
      cursor: "pointer",
      textDecoration: "underline",
    }),
    [],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "menuIdentifier",
        flex: 0.65,
        headerName: "",
        renderCell: (parameters: GridRenderCellParams<CampaignType>) => {
          if (parameters.row.scheduledAt) {
            const date = formatDate(new Date(parameters.row.scheduledAt), "MM/dd/yyyy, 'at' hh:mm a");
            return <RowActionsMenu rowMenuItems={makeScheduledCampaignActionsMenuItems(parameters.row.id, date)} />;
          }
          if (!parameters.row.sentCount) {
            return <RowActionsMenu rowMenuItems={makeCampaignActionsMenuItems(parameters.id.toString())} />;
          }

          return <RowActionsMenu rowMenuItems={makeSentCampaignActionsMenuItems(parameters.id.toString())} />;
        },
      },
      {
        field: "name",
        flex: 2,
        renderCell: (parameters: GridRenderCellParams<CampaignType>) => (
          <Typography sx={clickableStyles} variant="subtitle2">
            {parameters.value}
          </Typography>
        ),
        renderHeader: () => <ColumnHeader title="Campaign Name" tooltip="" />,
      },
      {
        field: "sentStatus",
        flex: 2,
        renderCell: (parameters: GridRenderCellParams<CampaignType>) => {
          const isSent = parameters.row.sentAt && !parameters.row.failedToSend;
          const isSendFailed = parameters.row.failedToSend;
          const scheduledAt = parameters.row.scheduledAt;

          if (scheduledAt) {
            const date = format(new Date(scheduledAt), "MM/dd/yy @ hh:mm a");
            return (
              <Tooltip title={`Scheduled for ${date}`}>
                <Box alignItems="center" display="flex" gap={1}>
                  <TimerRounded style={{ color: colors.purple[400] }} />
                  <Typography style={{ color: colors.purple[400], fontWeight: "bold" }} variant="body2">
                    Scheduled
                  </Typography>
                </Box>
              </Tooltip>
            );
          }

          if (isSendFailed) {
            return (
              <Box alignItems="center" display="flex" gap={1}>
                <ErrorRounded style={{ color: colors.red[600] }} />
                <Typography style={{ color: colors.red[600], fontWeight: "bold" }} variant="body2">
                  Issue
                </Typography>
              </Box>
            );
          }
          return (
            <Box alignItems="center" display="flex" gap={1}>
              {isSent ? (
                <>
                  <MarkEmailReadRoundedIcon style={{ color: colors.green[600] }} />
                  <Typography style={{ color: colors.green[600], fontWeight: "bold" }} variant="body2">
                    Sent
                  </Typography>
                </>
              ) : (
                <>
                  <SubjectRoundedIcon style={{ color: colors.orange[600] }} />
                  <Typography style={{ color: colors.orange[600], fontWeight: "bold" }} variant="body2">
                    Draft
                  </Typography>
                </>
              )}
            </Box>
          );
        },
        renderHeader: () => <ColumnHeader title="Status" tooltip="" />,
        valueGetter: (_: unknown, row: CampaignType) => (row.sentCount > 0 ? 1 : 0),
      },
      {
        field: "companyReach",
        flex: 2,
        renderHeader: () => (
          <ColumnHeader title="Companies" tooltip="The number of different companies reached with this Campaign." />
        ),
        valueGetter: (value: string) => Number.parseInt(value, 10),
      },
      {
        field: "contactReach",
        flex: 2,
        renderHeader: () => (
          <ColumnHeader title="Contacts" tooltip="The number of contacts that will be reached with this Campaign." />
        ),
        valueGetter: (value: string) => Number.parseInt(value, 10),
      },
      {
        field: "openRate",
        flex: 2,
        renderCell: (parameters: GridRenderCellParams<CampaignType>) => {
          const { openRatePercentage } = handleGaugeValue(parameters.row.emails);

          return (
            <Box alignItems="center" display="flex" gap={1}>
              <Typography>{openRatePercentage ? `${openRatePercentage}%` : "-"}</Typography>
            </Box>
          );
        },
        renderHeader: () => <ColumnHeader title="Open Rate" tooltip="" />,
        valueGetter: (value: string) => (value ? Number.parseInt(value, 10) : 0),
      },
      {
        field: "responseRate",
        flex: 2,
        renderCell: (parameters: GridRenderCellParams<CampaignType>) => {
          const { repliedRatePercentage } = handleGaugeValue(parameters.row.emails);

          return (
            <Box alignItems="center" display="flex" gap={1}>
              <Typography>{repliedRatePercentage ? `${repliedRatePercentage}%` : "-"}</Typography>
            </Box>
          );
        },
        renderHeader: () => <ColumnHeader title="Response Rate" tooltip="" />,
        valueGetter: (value: string) => (value ? Number.parseInt(value, 10) : 0),
      },
      {
        field: "sentAt",
        flex: 2,
        renderCell: (parameters: GridRenderCellParams<CampaignType>) => {
          if (parameters.row.sentAt) {
            return <Typography>{format(new Date(parameters.row.sentAt), "MM/dd/yy")}</Typography>;
          }
          return <Typography>-</Typography>;
        },
        renderHeader: () => <ColumnHeader title="Sent" tooltip="" />,
        type: "singleSelect",
      },
      {
        field: "updated",
        flex: 2,
        renderCell: (parameters: GridRenderCellParams<CampaignType>) => (
          <Typography>{format(new Date(parameters.row.updated), "MM/dd/yy")}</Typography>
        ),
        renderHeader: () => <ColumnHeader title="Updated" tooltip="" />,
        type: "dateTime",
        valueGetter: (value: string) => new Date(value),
      },
    ],
    [makeCampaignActionsMenuItems, clickableStyles],
  );

  return (
    <Box
      sx={{
        flexGrow: 1,
        width: "100%",
      }}
    >
      <Overlay
        caption="Please do not reload page."
        isVisible={cancelCampaignStatus === "pending"}
        title="Canceling Scheduled Campaign"
      />
      <DataGridPro
        checkboxSelection
        className="tab-height"
        columns={columns}
        disableColumnMenu={true}
        disableRowSelectionOnClick
        getRowClassName={getRowClassName}
        initialState={{
          sorting: {
            sortModel: [
              {
                field: "updated",
                sort: "desc",
              },
            ],
          },
        }}
        onCellClick={handleCellClick}
        onRowSelectionModelChange={(newSelection) => {
          setSelection(newSelection.map((newSelection) => newSelection.toString()));
        }}
        rows={campaigns}
        rowSelectionModel={selection}
        sx={{
          ...tableStyles.root,
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "rgb(255, 255, 255)",
          },
        }}
      />
    </Box>
  );
}
